import { createStore, createEffect, createEvent, combine } from 'effector';
import { $notificationStore } from '../notificationStore';
import { ApiUtils } from '@shared/utils';
import { AccountsApi, RequestStrategyEnum, type AccountByIdResponse, type AccountUpdateDetailsRequest, type WithSignal } from '@shared/api';

const getAccountEffect = createEffect(({ signal, ...request }: WithSignal<{ id: number }>) => AccountsApi.getAccount(request.id, signal));
const updateDetailsEffect = createEffect((request: AccountUpdateDetailsRequest) => AccountsApi.updateDetails(request));
const clearEvent = createEvent();

const $dataStore = createStore<AccountByIdResponse | null>(null)
  .on(getAccountEffect.done, (_, data) => data.result.data ?? null)
  .on(updateDetailsEffect.done, (current, data) => data.result.data ?? current)
  .reset(clearEvent);

const $loadingStore = createStore<boolean>(false)
  .on(getAccountEffect.pending, (_, data) => data)
  .on(getAccountEffect.fail, (_, data) => ApiUtils.abortApiLoadingFailCaseTrigger(data.error))
  .on(getAccountEffect.done, () => false)
  .reset(clearEvent);

const $errorStore = createStore<string | null>(null)
  .on(getAccountEffect, () => null)
  .on(getAccountEffect.fail, (_, data) => {
    if (data.error.message === "'Account Id' must not be empty. 'Account Id' must be greater than '0'.") return 'Action is forbidden.';
    if (data.error.message === 'Action is forbidden.') return 'Action is forbidden.';
    return 'Something went wrong while fetching Account. Please try again.';
  })
  .on(getAccountEffect.done, () => null)
  .reset(clearEvent);

const $actionErrorStore = createStore<string | null>(null)
  .on(updateDetailsEffect, () => null)
  .on(
    updateDetailsEffect.fail,
    (_, data) => ApiUtils.getApiErrorData(data.error, 'Something went wrong while updating Account. Please try again.').message,
  )
  .on(updateDetailsEffect.done, () => null)
  .reset(clearEvent);
$actionErrorStore.watch((error) => $notificationStore.events.setError(error));

const getAccountApi = ApiUtils.wrapperApi(getAccountEffect, RequestStrategyEnum.Abort);
clearEvent.watch(() => getAccountApi.cancel());

export const $accountByIdStore = {
  events: {
    getAccount: getAccountApi.trigger,
    updateDetails: updateDetailsEffect,
    clear: clearEvent,
  },
  store: combine({
    loading: $loadingStore,
    data: $dataStore,
    error: $errorStore,
  }),
};
